import { AUTHORS } from './authors';
import { PageMeta } from 'constants/pages';
import { BOOK_A_DEMO_LINK } from 'constants/common';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';

import HeaderImage from 'assets/resources/ai-live-sim-glossary/core-simulation-concepts.webp';
import SyntheticDataImage from 'assets/resources/ai-live-sim-glossary/synthetic-data-and-domain-techniques.webp';
import SensorsImage from 'assets/resources/ai-live-sim-glossary/sensors-and-perception-simulation.webp';
import AiMlAutonomyImage from 'assets/resources/ai-live-sim-glossary/ai-machine-learning-and-autonomy.webp';
import SafetyImage from 'assets/resources/ai-live-sim-glossary/safety-reliability-and-certification.webp';
import TestInfraImage from 'assets/resources/ai-live-sim-glossary/test-infrastructure-and-developmen-workflow.webp';
import MaritimeDefenseImage from 'assets/resources/ai-live-sim-glossary/maritime-and-defense-specific-terms.webp';

const commonData = {
  ...AUTHORS.AILiveSim,
  title: 'AILiveSim Glossary',
  publishDate: 'March 7, 2026',
  minutesOfRead: 12,
};

export const seventhMarch2026ArticleMeta: PageMeta = {
  url: '/articles/ailivesim-glossary',
  title: commonData.title,
  description:
    'A practical reference to clarify the key concepts shaping modern autonomous system testing, from multi-sensor simulation to safety validation and sim-to-real transfer.',
};

export const seventhMarch2026ArticleData: ArticleSections[] = [
  {
    resourceType: 'Article',
    image: HeaderImage,
    imageCaption: 'Core Simulation Concepts',
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    text: (
      <>
        <p>
          Simulation, synthetic data, sensor modeling, autonomy validation and the terminology
          around AI-driven development can quickly become complex.
        </p>
        <p>
          We&apos;ve created the AILiveSim Glossary as a practical reference to clarify the key
          concepts shaping modern autonomous system testing, from multi-sensor simulation to safety
          validation and sim-to-real transfer.
        </p>
        <p>
          Whether you&apos;re new to the field or deep in development, this guide helps align
          language with technology.
        </p>
      </>
    ),
    ...commonData,
  },

  {
    type: 'common',
    title: '1. Core Simulation Concepts',
    text: (
      <>
        <p>
          <strong>
            <a href="/articles/synthetic-environments-transform-training">
              AI-Enriched Simulation
            </a>
          </strong>
          <br />
          Simulation enhanced with AI to generate dynamic, realistic environments for autonomous
          system testing.
        </p>
        <p>
          <strong>
            <a href="/articles/physics-based-synthetic-data">Physics-Based Simulation</a>
          </strong>
          <br />
          Simulation that follows physical laws (gravity, aerodynamics, collisions, fluids) for
          realism.
        </p>
        <p>
          <strong>
            <a href="/automated-data-pipelines">Real-Time Simulation</a>
          </strong>
          <br />
          Simulation that runs at real-world speed, enabling live interaction or hardware
          integration.
        </p>
        <p>
          <strong>
            <a href="/articles/synthetic-environments-transform-training">
              Closed-Loop Simulation
            </a>
          </strong>
          <br />
          Testing where the system&apos;s decisions influence the environment, creating a feedback
          loop.
        </p>
        <p>
          <strong>
            <a href="/articles/scenario-plugins-simulation-framework">Scenario Generation</a>
          </strong>
          <br />
          Automatic or manual creation of test scenarios (weather, obstacles, behaviors).
        </p>
        <p>
          <strong>
            <a href="/whitepapers/using-synthetic-data-from-ailivesim">Synthetic Environment</a>
          </strong>
          <br />
          A fully virtual world representing real terrain, objects, and environmental conditions.
        </p>
        <p>
          <strong>
            <a href="/customer-case/autonomous-aircraft-taxiing">Digital Twin</a>
          </strong>
          <br />
          Virtual replica of a real-world asset or system for simulation and validation.
        </p>
        <p>
          <strong>
            <a href="/articles/synthetic-environments-transform-training">Ground Truth Data</a>
          </strong>
          <br />
          Perfectly accurate labels (object position, classification, distance) generated in
          simulation.
        </p>
        <p>
          <strong>
            <a href="/articles/scenario-plugins-simulation-framework">Scenario Replay</a>
          </strong>
          <br />
          Recording and reproducing simulation runs for debugging or validation.
        </p>
        <p>
          <strong>
            <a href="/all-industries">Simulation Asset Library</a>
          </strong>
          <br />
          Collection of reusable 3D models, environments, and scenarios.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '2. Synthetic Data & Domain Techniques',
    image: SyntheticDataImage,
    text: (
      <>
        <p>
          <strong>
            <a href="/whitepapers/using-synthetic-data-from-ailivesim">Synthetic Data</a>
          </strong>
          <br />
          Artificially generated data (images, LiDAR, radar, etc.) for training and testing AI.
        </p>
        <p>
          <strong>
            <a href="/articles/thermal-camera-sensors-ailivesim-platform">
              Synthetic Sensor Data Generation
            </a>
          </strong>
          <br />
          Creation of sensor-specific outputs (point clouds, radar returns, thermal images).
        </p>
        <p>
          <strong>
            <a href="/articles/data-augmentation-methods">Synthetic Data Augmentation</a>
          </strong>
          <br />
          Enhancing training datasets with simulation-generated variations.
        </p>
        <p>
          <strong>
            <a href="/platform">Domain Randomization</a>
          </strong>
          <br />
          Systematic variation of textures, lighting, weather, and objects to improve model
          robustness.
        </p>
        <p>
          <strong>
            <a href="/whitepapers/using-synthetic-data-from-ailivesim">Sim-to-Real Transfer</a>
          </strong>
          <br />
          Ability of models validated in simulation to perform reliably in the real world.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '3. Sensors & Perception Simulation',
    image: SensorsImage,
    text: (
      <>
        <p>
          <strong>
            <a href="/sensors">Sensor Simulation</a>
          </strong>
          <br />
          Reproducing outputs from cameras, LiDAR, radar, sonar, thermal sensors, etc.
        </p>
        <p>
          <strong>
            <a href="/sensors">Multi-Sensor Fusion</a>
          </strong>
          <br />
          Combining multiple sensor inputs to improve perception accuracy.
        </p>
        <p>
          <strong>
            <a href="/sensors">Ray Tracing</a>
          </strong>
          <br />
          High-fidelity light/path simulation used for realistic camera and LiDAR rendering.
        </p>
        <p>
          <strong>
            <a href="/sensors">LiDAR Simulation</a>
          </strong>
          <br />
          Generation of 3D point clouds with realistic noise and reflectivity.
        </p>
        <p>
          <strong>
            <a href="/sensors">Radar Simulation</a>
          </strong>
          <br />
          Emulation of radar returns including range, velocity, and material properties.
        </p>
        <p>
          <strong>
            <a href="/sensors">Thermal / Infrared Simulation</a>
          </strong>
          <br />
          Simulating heat signatures for IR-based perception systems.
        </p>
        <p>
          <strong>
            <a href="/sensors">Sensor Noise Modeling</a>
          </strong>
          <br />
          Simulating imperfections such as blur, occlusion, and reflections.
        </p>
        <p>
          <strong>
            <a href="/sensors">Sonar Simulation (Maritime/Defense)</a>
          </strong>
          <br />
          Underwater acoustic sensor modeling.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '4. AI, Machine Learning & Autonomy',
    image: AiMlAutonomyImage,
    text: (
      <>
        <p>
          <strong>
            <a href="/articles/synthetic-environments-transform-training">Perception Algorithm</a>
          </strong>
          <br />
          AI models that detect, classify, and track objects from sensor data.
        </p>
        <p>
          <strong>
            <a href="/applications/automatic-dataset-enhancement">Model Validation</a>
          </strong>
          <br />
          Testing AI models under diverse simulated conditions to assess accuracy and robustness.
        </p>
        <p>
          <strong>
            <a href="/platform">Autonomous System Validation</a>
          </strong>
          <br />
          Full-system testing of perception, planning, and control across the ODD.
        </p>
        <p>
          <strong>
            <a href="/articles/path-planning-for-autonomous-ships">Path Planning</a>
          </strong>
          <br />
          Algorithmic calculation of safe and efficient navigation paths.
        </p>
        <p>
          <strong>
            <a href="/articles/collision-avoidance-algorithms">Control Algorithm</a>
          </strong>
          <br />
          Software translating navigation decisions into motion (steering, throttle, braking).
        </p>
        <p>
          <strong>
            <a href="/articles/auto-docking-simulation-maritime">
              Operational Design Domain (ODD)
            </a>
          </strong>
          <br />
          All environmental and operational conditions intended for autonomous system use.
        </p>
        <p>
          <strong>
            <a href="/applications/automatic-dataset-enhancement">Edge Case</a>
          </strong>
          <br />
          Rare or extreme events difficult to test in real life but easily simulated.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '5. Safety, Reliability & Certification',
    image: SafetyImage,
    text: (
      <>
        <p>
          <strong>
            <a href="/customer-case/anti-collision-systems">Safety Validation</a>
          </strong>
          <br />
          Ensuring systems behave safely across all scenarios, including worst-case events.
        </p>
        <p>
          <strong>
            <a href="/technical-briefs/classical-simulation-testing-doesnt-scale">
              Failure Mode Testing
            </a>
          </strong>
          <br />
          Evaluating system behavior during sensor faults or unexpected failures.
        </p>
        <p>
          <strong>
            <a href="/articles/auto-docking-simulation-maritime">Coverage Analysis</a>
          </strong>
          <br />
          Measuring what percentage of the ODD has been tested in simulation.
        </p>
        <p>
          <strong>
            <a href="/articles/autonomous-aircraft-taxiing">ISO 26262 / Safety Standards</a>
          </strong>
          <br />
          Functional safety frameworks addressed using simulation.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '6. Test Infrastructure & Development Workflow',
    image: TestInfraImage,
    text: (
      <>
        <p>
          <strong>
            <a href="/articles/simulation-in-the-loop-sil-save-time-money">
              Simulation-in-the-Loop (SiL)
            </a>
          </strong>
          <br />
          A closed-loop approach in which the simulator acts as the primary
          orchestrator&mdash;providing the operational context and inputs that drive end-to-end
          development and validation.
        </p>
        <p>
          <strong>
            <a href="/articles/model-in-the-loop-simulation">Model-in-the-Loop (MiL)</a>
          </strong>
          <br />
          Operates at the earliest stage of development, where both the controller and the physical
          plant are represented as abstract models, with no compiled code or real hardware present,
          useful for rapid algorithm prototyping, but limited in fidelity.
        </p>
        <p>
          <strong>
            <a href="/">Software-in-the-Loop</a>
          </strong>
          <br />
          Advances one step further by replacing the abstract model with actual compiled code running
          against a simulated environment; its primary purpose is verifying model-code equivalence
          and catching software regressions before hardware enters the picture.
        </p>
        <p>
          <strong>
            <a href="/">Hardware-in-the-Loop (HiL)</a>
          </strong>
          <br />
          Connects real physical computing hardware, such as ECUs or embedded processors, directly to
          the simulation, validating real-time timing, signal integrity, and hardware-software
          interactions that neither MiL nor SWiL can replicate.
        </p>
        <p>
          <strong>
            <a href="/">Human-on-the-Loop</a>
          </strong>
          <br />
          Introduces a human operator as an additional oversight layer, the human is not the system
          under test, but an external decision-maker who interacts with the loop to assess usability,
          safety, and edge-case judgment.
        </p>
        <p>
          <strong>
            <a href="/all-applications">
              Continuous Integration / Continuous Development (CI/CD)
            </a>
          </strong>
          <br />
          Automated simulation tests that run whenever code changes, ensuring consistent validation.
        </p>
        <p>
          <strong>Parallelization</strong>
          <br />
          Running multiple simulations simultaneously to accelerate testing.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '7. Maritime & Defense-Specific Terms',
    image: MaritimeDefenseImage,
    text: (
      <>
        <p>
          <strong>
            <a href="/articles/virtual-sea-trials-what-simulation-testing-reveals-about-maritime-surveillance-performance">
              Sea State Simulation
            </a>
          </strong>
          <br />
          Modeling waves, currents, and weather for maritime autonomy.
        </p>
        <p>
          <strong>
            <a href="/Industries/navy-defence">Battlefield/Defense Simulation (general)</a>
          </strong>
          <br />
          Use of simulation for mission-critical AI testing such as detection, navigation, and
          threat avoidance.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Ready to Take the Next Step?',
    text: (
      <>
        <p>
          Our simulation platform helps teams building autonomous systems generate sensor data,
          build training and validation datasets, and stress-test across edge cases, all within
          physics-validated 3D environments.
        </p>
        <p>
          If you&apos;d like to see how this works in practice, we&apos;d be happy to walk you
          through it.
        </p>
        <p>
          <strong>
            <a href={BOOK_A_DEMO_LINK} target="_blank" rel="noopener noreferrer">
              Book Your Demo Today
            </a>
          </strong>
        </p>
        <p>
          <strong>
            <a href="/resources">
              Subscribe to our newsletter
            </a>
          </strong>
        </p>
      </>
    ),
  },
];

export const seventhMarch2026ArticleCard: TCardProps = {
  description:
    'A practical glossary covering simulation, synthetic data, sensor modeling, autonomy validation and the key terminology around AI-driven autonomous system testing.',
  image: HeaderImage,
  type: 'Article',
  category: 'articles',
  postUrl: seventhMarch2026ArticleMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.SyntheticData,
    EHashtag.AISimulation,
    EHashtag.ComputerVision,
    EHashtag.SafetyValidation,
    EHashtag.DefenseTechnology,
    EHashtag.MaritimeTechnology,
  ],
};
